import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DatabaseOutlined, SafetyOutlined, SearchOutlined, TableOutlined, WarningOutlined } from '@ant-design/icons';

import { Bento, PageHeader, TabsTruot, Tile, TileBody } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { DanhSachVai } from '../components/quyen/DanhSachVai';
import { HangBang } from '../components/quyen/HangBang';
import { QuyenCuaToi } from '../components/quyen/QuyenCuaToi';
import { QuyenKhuonTab } from '../components/quyen/QuyenKhuonTab';
import { QUYEN } from '../constants';
import { MUC_QUYEN, THU_TU_MUC_QUYEN } from '../constants-bot';
import { useDanhSachNguon, useVaiTroToChuc } from '../hooks/useChung';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import { useLuuMaTranQuyen, useMaTranQuyen, useTaiKhoanQuyen } from '../hooks/useQuyen';
import {
  LOC_BANG,
  batBang,
  camCot,
  coThayDoi,
  datMuc,
  gopDanhSachVai,
  locDanhSachBang,
  tomTatQuyen,
  type BanNhapQuyen,
  type LocBang,
} from '../nghiep-vu/quyen-du-lieu';
import type { PermGrant } from '../types';

const KHOI = 'rounded-lg border border-default bg-surface shadow-sm';

/**
 * Phân quyền dữ liệu (`/client/tro-ly-ai/quyen-du-lieu`), port `DataPermView` của native: ai đọc được bảng nào, cột nào —
 * của từng nguồn.
 *
 * Hai cột (vai trái · bảng phải): phân quyền là ma trận vai × bảng, nhưng người vận hành sửa TỪNG vai một. Lưu cột BỊ CẤM,
 * không lưu cột được xem — cột mới đồng bộ về mặc định hiện ra, thay vì bị giấu im lặng với mọi người.
 *
 * Danh sách vai: `PermAccounts.accounts` của module (giữ `label`, `canManage`) gộp với vai của tổ chức từ API vai trò của
 * platform — module không tự liệt kê được vai. Vai tổ chức chưa có dòng trong ma trận hiện ở mức "Không đọc gì" (mặc
 * định đóng) cho tới khi có người cấp; bản lưu chỉ gửi những dòng máy chủ đã có hoặc vừa được cấp.
 *
 * Sửa được khi máy chủ nói `canManage` VÀ người dùng có `tro-ly-ai:quyen-du-lieu:update`.
 */
export function DataPermView() {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const duongDan = useDuongDan();
  const q = useModulePermissions(QUYEN);

  const [sourceIdTho, setSourceId] = useState('');
  const [vaiTho, setVai] = useState('');
  const [tuKhoa, setTuKhoa] = useState('');
  const [locBang, setLocBang] = useState<LocBang>('tat_ca');
  // Hai ma trận, hai tab, hai bản nháp — nút Lưu ở đầu trang đi theo tab đang mở.
  const [tabChon, setTab] = useState<'bang' | 'khuon'>('bang');
  const [nhapTho, setNhap] = useState<{ sourceId: string; du: BanNhapQuyen } | null>(null);

  const sources = useDanhSachNguon();
  const accounts = useTaiKhoanQuyen();
  const canManage = !!accounts.data?.canManage;
  const vaiToChuc = useVaiTroToChuc(canManage);

  const sourceId =
    sources.data?.some((s) => s.id === sourceIdTho) ? sourceIdTho : (sources.data?.[0]?.id ?? '');
  // Nguồn API không có bảng nào: quyền của nó chỉ có một trục — dùng khuôn nào.
  const laApi = sources.data?.find((x) => x.id === sourceId)?.kind === 'api';
  const tab: 'bang' | 'khuon' = laApi ? 'khuon' : tabChon;

  const matrix = useMaTranQuyen(sourceId, !laApi && canManage);
  const luu = useLuuMaTranQuyen(sourceId);

  const dsVai = useMemo(() => gopDanhSachVai(accounts.data?.accounts, vaiToChuc.data), [accounts.data, vaiToChuc.data]);
  const vai = dsVai.some((a) => a.role === vaiTho) ? vaiTho : (dsVai[0]?.role ?? '');

  const goc = useMemo<BanNhapQuyen>(
    () => Object.fromEntries((matrix.data?.grants ?? []).map((g) => [g.role, g])),
    [matrix.data],
  );
  const coNhap = nhapTho && nhapTho.sourceId === sourceId ? nhapTho.du : null;
  const nhap = coNhap ?? goc;
  const grant: PermGrant | undefined = nhap[vai];
  const tables = matrix.data?.tables ?? [];
  const doi = coThayDoi(coNhap, matrix.data?.grants);
  const khoa = !(q.quyenDuLieuSua && canManage);

  const bangDaMo = useMemo(() => new Set((grant?.tables ?? []).map((x) => x.table)), [grant?.tables]);
  const bangLoc = useMemo(() => locDanhSachBang(tables, tuKhoa, locBang, bangDaMo), [tables, tuKhoa, locBang, bangDaMo]);

  const sua = (fn: (n: BanNhapQuyen) => BanNhapQuyen) => setNhap({ sourceId, du: fn(nhap) });

  // Đổi nguồn là đổi hẳn danh sách bảng: từ khoá của nguồn cũ gần như chắc chắn không khớp gì.
  const doiNguon = (id: string) => {
    setSourceId(id);
    setTuKhoa('');
    setLocBang('tat_ca');
  };

  const tomTat = (role: string) => {
    const tt = tomTatQuyen(nhap[role]);
    switch (tt.ma) {
      case 'none':
        return t('troLyAiBotQuyenDuLieu.tomTatNone');
      case 'full':
        return t('troLyAiBotQuyenDuLieu.tomTatFull');
      case 'chuaMo':
        return t('troLyAiBotQuyenDuLieu.tomTatChuaMo');
      case 'bang':
        return t('troLyAiBotQuyenDuLieu.tomTatBang', { n: soVN(tt.n, locale) });
      default:
        return t('troLyAiBotQuyenDuLieu.tomTatBangGiau', { n: soVN(tt.n, locale), c: soVN(tt.c, locale) });
    }
  };

  const luuNgay = () =>
    luu.mutate(
      Object.values(nhap).filter((g) => g.role in goc || g.level !== 'none'),
      {
        onSuccess: () => {
          toast.success(t('troLyAiBotQuyenDuLieu.daLuuPhanQuyen'));
          setNhap(null);
        },
        onError: (e) => toast.error(moTaLoi(e)),
      },
    );

  const header = (actions?: ReactNode) => (
    <PageHeader
      breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.phanQuyenDuLieu') }]}
      titleIcon={<SafetyOutlined className="text-primary-600" />}
      title={t('troLyAiBotQuyenDuLieu.tieuDe')}
      subtitle={t('troLyAiBotQuyenDuLieu.phuDe')}
      actions={actions}
    />
  );

  // ── Trạng thái của cả màn: đang tải / lỗi / không sửa được / chưa có nguồn ─────────────────────────────────────
  let than: ReactNode;
  if (accounts.isPending || sources.isPending) {
    than = (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  } else if (accounts.error || sources.error) {
    than = (
      <div className={KHOI}>
        <DaiLoi
          loi={accounts.error ?? sources.error}
          onThuLai={() => {
            void accounts.refetch();
            void sources.refetch();
          }}
        />
      </div>
    );
  } else if (!canManage) {
    // Ma trận cho biết dữ liệu nhạy cảm nằm ở đâu, nên việc XEM nó cũng cần quyền riêng.
    than = (
      <div className="space-y-4">
        <section className={cn(KHOI, 'space-y-3 p-8 text-center')}>
          <SafetyOutlined aria-hidden className="text-3xl text-content-muted" />
          <p className="text-sm font-medium text-content-primary">{t('troLyAiBotQuyenDuLieu.khongSuaDuoc')}</p>
          <p className="mx-auto max-w-md text-xs leading-relaxed text-content-secondary">
            {t('troLyAiBotQuyenDuLieu.khongSuaDuocGhi')}
          </p>
        </section>
        <div className={KHOI}>
          <QuyenCuaToi nguon={sources.data} />
        </div>
      </div>
    );
  } else if (!sources.data?.length) {
    than = (
      <div className={KHOI}>
        <Empty
          icon={DatabaseOutlined}
          title={t('troLyAiBotQuyenDuLieu.chuaCoNguon')}
          description={t('troLyAiBotQuyenDuLieu.chuaCoNguonMo')}
          action={
            <Link to={duongDan('/tro-ly-ai/ket-noi')}>
              <Button variant="primary">{t('troLyAiBotQuyenDuLieu.sangKetNoi')}</Button>
            </Link>
          }
        />
      </div>
    );
  } else {
    const noiDungBang = (
      <div className="space-y-4">
        {matrix.error ? (
          <div className={KHOI}>
            <DaiLoi loi={matrix.error} onThuLai={() => void matrix.refetch()} />
          </div>
        ) : null}
        <Bento>
          <DanhSachVai
            ds={dsVai}
            chon={vai}
            onChon={setVai}
            tomTat={tomTat}
            cham={(role) => MUC_QUYEN[nhap[role]?.level ?? 'none'].cham}
          />

          <Tile span="lg:col-span-9" className="min-h-[20rem] min-w-0">
            <TileBody>
              {matrix.isPending ? (
                <div className="space-y-2.5" aria-busy="true">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-14 rounded" />
                  ))}
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <div role="group" aria-label={t('troLyAiBotQuyenDuLieu.mucQuyen')} className="inline-flex rounded bg-subtle p-0.5">
                      {THU_TU_MUC_QUYEN.map((l) => (
                        <button
                          key={l}
                          type="button"
                          disabled={khoa || !vai}
                          aria-pressed={(grant?.level ?? 'none') === l}
                          onClick={() => sua((n) => datMuc(n, vai, l))}
                          className={cn(
                            'rounded px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed',
                            (grant?.level ?? 'none') === l
                              ? 'bg-surface text-primary-700 shadow-sm'
                              : 'text-content-secondary hover:text-content-primary',
                          )}
                        >
                          {t(MUC_QUYEN[l].khoa)}
                        </button>
                      ))}
                    </div>
                    {grant?.level === 'full' ? (
                      <span className="flex items-center gap-1 text-xs text-warning-700">
                        <WarningOutlined className="text-xs" />
                        {t('troLyAiBotQuyenDuLieu.canhBaoFull')}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-4">
                    {grant?.level !== 'tables' ? (
                      <div className="flex flex-col items-center gap-2 py-12 text-center">
                        <TableOutlined aria-hidden className="text-2xl text-content-muted" />
                        <p className="text-xs text-content-muted">
                          {grant?.level === 'full'
                            ? t('troLyAiBotQuyenDuLieu.fullMoTa')
                            : t('troLyAiBotQuyenDuLieu.chuaMoBang')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            allowClear
                            className="max-w-xs"
                            prefix={<SearchOutlined className="text-content-muted" />}
                            aria-label={t('troLyAiBotQuyenDuLieu.timBang')}
                            placeholder={t('troLyAiBotQuyenDuLieu.timBang')}
                            value={tuKhoa}
                            onChange={(e) => setTuKhoa(e.target.value)}
                          />
                          <div className="inline-flex rounded bg-subtle p-0.5">
                            {LOC_BANG.map((l) => (
                              <button
                                key={l}
                                type="button"
                                aria-pressed={locBang === l}
                                onClick={() => setLocBang(l)}
                                className={cn(
                                  'rounded px-2.5 py-1 text-xs font-medium transition',
                                  locBang === l
                                    ? 'bg-surface text-primary-700 shadow-sm'
                                    : 'text-content-secondary hover:text-content-primary',
                                )}
                              >
                                {t(`troLyAiBotQuyenDuLieu.locBang.${l}`)}
                              </button>
                            ))}
                          </div>
                          <span className="ms-auto text-xs tabular-nums text-content-muted">
                            {t('troLyAiBotQuyenDuLieu.demBang', {
                              mo: soVN(bangDaMo.size, locale),
                              tong: soVN(tables.length, locale),
                              hien: soVN(bangLoc.length, locale),
                            })}
                          </span>
                        </div>
                        {/* Cuộn RIÊNG danh sách: nguồn hàng nghìn bảng thì cuộn cả trang đẩy thanh mức và ô tìm khỏi tầm nhìn. */}
                        <div className="max-h-[65vh] space-y-2 overflow-y-auto pe-1">
                          {/* Rỗng vì bộ lọc: chỉ một câu, không mô tả, không nút. */}
                          {!bangLoc.length ? (
                            <p className="py-8 text-center text-xs text-content-muted">
                              {t('troLyAiBotQuyenDuLieu.khongKhopBang')}
                            </p>
                          ) : null}
                          {bangLoc.map((b) => {
                            const cap = grant.tables.find((x) => x.table === b.name);
                            return (
                              <HangBang
                                key={b.name}
                                table={b}
                                denied={cap?.deniedColumns}
                                on={!!cap}
                                readOnly={khoa}
                                onToggle={(v) => sua((n) => batBang(n, vai, b.name, v))}
                                onCol={(col, cam) => sua((n) => camCot(n, vai, b.name, col, cam))}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-12">
            <QuyenCuaToi nguon={sources.data} />
          </Tile>
        </Bento>
      </div>
    );

    than = (
      <TabsTruot
        key={`${sourceId}:${laApi ? 'api' : 'bang'}`}
        defaultActiveKey={tab}
        onChange={(k) => setTab(k as 'bang' | 'khuon')}
        items={[
          ...(laApi ? [] : [{ key: 'bang', label: t('troLyAiBotQuyenDuLieu.tabTheoBang'), children: noiDungBang }]),
          {
            key: 'khuon',
            label: t('troLyAiBotQuyenDuLieu.tabTheoKhuon'),
            children: <QuyenKhuonTab sourceId={sourceId} dsVai={dsVai} suaDuoc={!khoa} />,
          },
        ]}
      />
    );
  }

  const coNguon = canManage && !!sources.data?.length;
  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {header(
          coNguon ? (
            <div className="flex flex-wrap items-center gap-2">
              <Select<string>
                aria-label={t('troLyAiBotQuyenDuLieu.nguon')}
                className="w-52"
                value={sourceId || undefined}
                onChange={doiNguon}
                options={(sources.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              />
              {tab === 'khuon' ? null : !khoa ? (
                <Button variant="primary" size="md" disabled={!doi} loading={luu.isPending} onClick={luuNgay}>
                  {doi ? t('troLyAiBotQuyenDuLieu.luuThayDoi') : t('troLyAiBotQuyenDuLieu.daLuu')}
                </Button>
              ) : (
                <Badge variant="neutral">{t('troLyAiBotQuyenDuLieu.chiXem')}</Badge>
              )}
            </div>
          ) : undefined,
        )}
        {than}
      </div>
    </div>
  );
}
